import { BACKGROUND_PATTERN_ID } from '@/domain/selection/reservedSelectionIds';
import { isMaskUrl, maskShapeOf } from '@/domain/image/maskZone';
import { LOCKED_FOREGROUND_GROUP_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { Ellipse, FabricImage, Group, Rect, Triangle } from 'fabric';
import type { Canvas, FabricObject } from 'fabric';

type TMaskClipServiceParams = {
    canvas: Canvas;
};

type TTaggedObject = FabricObject & { id?: string; isMaskZone?: boolean };

// Mask zones (devis complémentaire n°2): an image field drawn as a hatched shape, taken or not,
// that makes transparent whatever the stack holds under it. Fabric has no "erase what is below",
// so each object under a mask gets an inverted clip made of the masks above it; the clip is
// rebuilt on the frame that follows any change of the stack or of a mask (the signature below),
// so a mask dragged, resized, rotated, restacked or deleted cuts at once, on the canvas and,
// through the texture, on the cup. At rest a mask draws nothing: only the hole shows. It draws
// its hatching while it is the selection, and the exports remove it (see stripCanvasOverlays).
class MaskClipService {
    private readonly _canvas: Canvas;
    private _signature = '';

    constructor(params: TMaskClipServiceParams) {
        this._canvas = params.canvas;
        this._canvas.on('before:render', this._refresh);
    }

    dispose(): void {
        this._canvas.off('before:render', this._refresh);
    }

    private readonly _refresh = (): void => {
        const objects = this._canvas.getObjects() as TTaggedObject[];
        const masks = objects.filter(_isMask);
        const activeObject = this._canvas.getActiveObject();
        masks.forEach((mask) => {
            mask.opacity = mask === activeObject ? 1 : 0;
        });

        const signature = objects.map((object, index) => `${index}:${_keyOf(object)}`).join('|');
        if (signature === this._signature) {
            return;
        }
        this._signature = signature;

        objects.forEach((object, index) => {
            if (!_isClipTarget(object)) {
                return;
            }
            const masksAbove = masks.filter((mask) => objects.indexOf(mask) > index);
            object.set({ clipPath: masksAbove.length > 0 ? _buildClip(masksAbove) : undefined, dirty: true });
        });
    };
}

function _isMask(object: TTaggedObject): boolean {
    return object.isMaskZone === true;
}

// What an object contributes to the signature: its place in the stack, and for a mask its
// geometry, since that is what the clips copy.
function _keyOf(object: TTaggedObject): string {
    if (!_isMask(object)) {
        return object.id ?? '';
    }
    return [object.id, object.left, object.top, object.width, object.height, object.scaleX, object.scaleY, object.angle].join(',');
}

// What a mask can cut: the fields, the background motif and the group of locked elements. The
// working zone, the grid and the cutting overlays are chrome, not artwork.
function _isClipTarget(object: TTaggedObject): boolean {
    if (_isMask(object)) {
        return false;
    }
    return (
        object.id === BACKGROUND_PATTERN_ID ||
        object.id === LOCKED_FOREGROUND_GROUP_NAME ||
        object.type.toLowerCase() === 'image' ||
        object.type.toLowerCase() === 'textbox'
    );
}

function _buildClip(masks: TTaggedObject[]): Group {
    const shapes = masks.map(_toShape);
    return new Group(shapes, { absolutePositioned: true, inverted: true });
}

function _toShape(mask: TTaggedObject): FabricObject {
    const image = mask as FabricImage;
    const placement = {
        angle: image.angle,
        flipX: image.flipX,
        flipY: image.flipY,
        left: image.left,
        scaleX: image.scaleX,
        scaleY: image.scaleY,
        top: image.top,
    };
    switch (maskShapeOf(image.getSrc())) {
        case 'rond':
            return new Ellipse({ ...placement, rx: image.width / 2, ry: image.height / 2 });
        case 'triangle':
            return new Triangle({ ...placement, height: image.height, width: image.width });
        default:
            return new Rect({ ...placement, height: image.height, width: image.width });
    }
}

// Whether a fabric object loaded from a saved canvas (an export clone) is a mask.
function isMaskImage(object: FabricObject): boolean {
    return object instanceof FabricImage && isMaskUrl(object.getSrc());
}

export { isMaskImage, MaskClipService };
